/**
 * Human-readable rendering of `inventory_movements.note`.
 *
 * The column is free `text` and almost every writer puts plain prose in it (a
 * receive note, a write-off reason, a POS refund reason). One writer does not:
 * the kit-component fallback in `services/kit-components.ts` stores a JSON blob
 * when a kit is short on its own stock and is deducted as its components
 * instead. That blob was reaching the Stock Movements report — table and CSV
 * alike — as a raw `{"kit_fallback":true,...}` string.
 *
 * Formatting happens on READ rather than at the write site on purpose: the JSON
 * is a useful structured trail, nothing else parses it, and rewriting the
 * writer would leave every row already in the database unreadable.
 *
 * A second structured writer joined it: a recipe's MATERIAL consumption and its
 * restock (`services/product-materials.ts`, marker in `./product-materials`).
 * That one IS parsed back, by the restock path, which is why its shape lives in
 * a module of its own.
 */
import { decodeMaterialNote } from './product-materials';

/** The kit-fallback note shape. */
export type KitFallbackNote = {
  kit_fallback: true;
  kit_product_id?: string;
  kit_qty?: number;
};

function asRecord(note: string): Record<string, unknown> | null {
  const t = note.trim();
  // Cheap gate first: the overwhelming majority of notes are prose, and
  // JSON.parse on prose throws for every single row.
  if (!t.startsWith('{')) return null;
  try {
    const v: unknown = JSON.parse(t);
    return v !== null && typeof v === 'object' && !Array.isArray(v)
      ? (v as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/** Parsed kit-fallback payload, or null when the note isn't one. */
export function parseKitFallbackNote(note: string | null | undefined): KitFallbackNote | null {
  if (!note) return null;
  const rec = asRecord(note);
  if (!rec || rec.kit_fallback !== true) return null;
  return {
    kit_fallback: true,
    kit_product_id: typeof rec.kit_product_id === 'string' ? rec.kit_product_id : undefined,
    kit_qty: typeof rec.kit_qty === 'number' ? rec.kit_qty : undefined,
  };
}

export type FormatInventoryNoteOpts = {
  /** Resolved name for `kit_product_id`, when the caller could look it up. */
  kitProductName?: string | null;
};

/**
 * Render a note for display. Prose passes through untouched; the kit-fallback
 * blob becomes a sentence; any other JSON object degrades to `key: value` pairs
 * rather than leaking braces and quotes into a spreadsheet cell.
 */
export function formatInventoryNote(
  note: string | null | undefined,
  opts: FormatInventoryNoteOpts = {},
): string {
  if (note == null) return '';
  const trimmed = note.trim();
  if (trimmed === '') return '';

  const kit = parseKitFallbackNote(trimmed);
  if (kit) {
    const qty = kit.kit_qty != null ? ` (×${kit.kit_qty})` : '';
    const name = opts.kitProductName?.trim();
    return name
      ? `Auto-deducted as components of "${name}"${qty}`
      : `Auto-deducted as kit components${qty}`;
  }

  const material = decodeMaterialNote(trimmed);
  if (material) {
    const sold = material.sold_name?.trim();
    if (material.restock) {
      const from = sold ? `Returned from "${sold}"` : 'Returned from a reversed sale';
      const reason = material.reason?.trim();
      return reason ? `${from}: ${reason}` : from;
    }
    const used = sold ? `Used by "${sold}"` : 'Used by a recipe';
    // A shortfall means the shelf held less than the recipe called for, so the
    // count was already wrong before this sale. Say it, or nobody recounts.
    return material.short > 0
      ? `${used} (needed ${material.required}, short ${material.short})`
      : used;
  }

  const rec = asRecord(trimmed);
  if (rec) {
    const pairs = Object.entries(rec).map(
      ([k, v]) => `${k.replace(/_/g, ' ')}: ${v == null ? '' : String(v)}`,
    );
    return pairs.join('; ');
  }

  return trimmed;
}
